import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '../ui/select';
import { BatteryCharging, AlertCircle, AlertTriangle, CheckCircle2, CarFront, RotateCcw } from 'lucide-react';
import {
  CONSUMPTION_BASES, DEFAULT_INPUTS, FIELD_SPECS, SOC_PRESETS,
  isFieldActive, validateCrossFields, validateField,
} from '../../lib/evBatteryTools';
import { BATTERY_SIZING_PRESETS, GENERIC_PRESET_ID, findBatteryPreset } from '../../lib/evBatterySizingPresets';
import { batteryToolApi } from '../../lib/toolsApi';

const CALC_DEBOUNCE_MS = 350;

const SPEC_BY_KEY = Object.fromEntries(FIELD_SPECS.map((s) => [s.key, s]));
const BASIS_BY_VALUE = Object.fromEntries(CONSUMPTION_BASES.map((b) => [b.value, b]));

const GROUPS = [
  { id: 'vehicle', title: 'Vehicle & Drive', keys: ['rangeKm', 'consumptionWhKm', 'packVoltageV', 'auxPowerW', 'chargingEffPct', 'drivelineEffPct', 'regenRecoveryPct', 'avgSpeedKmph'] },
  { id: 'motor', title: 'Motor', keys: ['motorContKw', 'motorPeakKw', 'motorInvEffPct'] },
  { id: 'cell', title: 'Cell Specification', keys: ['cellNomV', 'cellMinV', 'cellMaxV', 'cellCapacityAh', 'cRate', 'pulseCRate', 'cellMassKg', 'cellToPackMassPct'] },
  { id: 'soc', title: 'SOC Window', keys: ['minSocPct', 'maxSocPct'] },
  { id: 'margin', title: 'Design Margins', keys: ['eolCapacityPct', 'coldDeratePct'] },
];

const NUMERIC_KEYS = FIELD_SPECS.map((s) => s.key);

function toRawStrings(defaults) {
  return Object.fromEntries(NUMERIC_KEYS.map((k) => [k, String(defaults[k])]));
}

const kWh = (wh) => (wh / 1000).toFixed(1);
const fixed = (n, d) => Number(n).toFixed(d);

const Stat = ({ label, value, sub }) => (
  <div>
    <p className="font-mono text-xs uppercase tracking-wider text-muted-foreground mb-1">{label}</p>
    <p className="font-mono text-sm font-bold">{value}</p>
    {sub && <p className="font-mono text-[11px] text-muted-foreground mt-0.5">{sub}</p>}
  </div>
);

const Row = ({ label, value }) => (
  <div className="flex items-center justify-between gap-3 font-mono text-xs">
    <span className="text-muted-foreground">{label}</span>
    <span className="font-bold text-right">{value}</span>
  </div>
);

const EvBatterySizingCalculator = () => {
  const [presetId, setPresetId] = useState(GENERIC_PRESET_ID);
  const preset = findBatteryPreset(presetId);
  const [basis, setBasis] = useState(DEFAULT_INPUTS.consumptionBasis);
  const [raw, setRaw] = useState(() => toRawStrings(DEFAULT_INPUTS));

  const setField = (key, value) => setRaw((prev) => ({ ...prev, [key]: value }));

  // Loads every input (and the consumption basis) from a vehicle preset in one go.
  const loadPreset = (id) => {
    const p = findBatteryPreset(id);
    if (!p) return;
    setPresetId(id);
    setBasis(p.inputs.consumptionBasis);
    setRaw(toRawStrings(p.inputs));
  };

  const presetModified = !!preset && (basis !== preset.inputs.consumptionBasis
    || NUMERIC_KEYS.some((k) => raw[k] !== String(preset.inputs[k])));

  // Only fields used by the selected consumption basis are validated and shown; a hidden
  // field is sent as entered if valid, else with its default, so a stale invalid entry
  // can't block the calculation.
  const { fieldErrors, values } = useMemo(() => {
    const errors = {};
    const parsed = {};
    for (const spec of FIELD_SPECS) {
      if (!isFieldActive(spec, basis)) {
        parsed[spec.key] = validateField(spec, raw[spec.key]) ? DEFAULT_INPUTS[spec.key] : Number(raw[spec.key]);
        continue;
      }
      const err = validateField(spec, raw[spec.key]);
      if (err) errors[spec.key] = err;
      else parsed[spec.key] = Number(raw[spec.key]);
    }
    validateCrossFields(parsed, errors);
    return { fieldErrors: errors, values: { ...parsed, consumptionBasis: basis } };
  }, [raw, basis]);

  const hasErrors = Object.keys(fieldErrors).length > 0;

  const [results, setResults] = useState(null);
  const [crossCheck, setCrossCheck] = useState(null);
  const [calcError, setCalcError] = useState(null);
  const requestIdRef = useRef(0);

  useEffect(() => {
    if (hasErrors) {
      setResults(null);
      setCrossCheck(null);
      setCalcError(null);
      return;
    }
    const requestId = ++requestIdRef.current;
    const timer = setTimeout(() => {
      batteryToolApi.sizeBattery(values)
        .then((outcome) => {
          if (requestId !== requestIdRef.current) return;
          if (outcome.errors) {
            setResults(null);
            setCrossCheck(null);
            setCalcError(Object.values(outcome.errors)[0]);
          } else {
            setResults(outcome.results);
            setCrossCheck(outcome.crossCheck);
            setCalcError(null);
          }
        })
        .catch((e) => {
          if (requestId !== requestIdRef.current) return;
          setResults(null);
          setCrossCheck(null);
          setCalcError(e.message);
        });
    }, CALC_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [values, hasErrors]);

  const renderField = (key) => {
    const spec = SPEC_BY_KEY[key];
    if (!isFieldActive(spec, basis)) return null;
    const error = fieldErrors[key];
    return (
      <div className="space-y-2" key={key}>
        <Label htmlFor={`ev-${key}`} className="font-mono text-xs uppercase tracking-wider text-muted-foreground">
          {spec.label} <span className="normal-case text-muted-foreground/70">({spec.unit})</span>
        </Label>
        <Input
          id={`ev-${key}`}
          type="number"
          step="any"
          value={raw[key]}
          onChange={(e) => setField(key, e.target.value)}
          className="font-mono"
          aria-invalid={!!error}
          aria-describedby={error ? `ev-${key}-error` : spec.help ? `ev-${key}-help` : undefined}
          data-testid={`ev-${key}-input`}
        />
        {error ? (
          <p id={`ev-${key}-error`} className="flex items-center gap-1 text-xs text-destructive" data-testid={`ev-${key}-error`}>
            <AlertCircle className="h-3 w-3 shrink-0" /> {error}
          </p>
        ) : spec.help ? (
          <p id={`ev-${key}-help`} className="text-[11px] leading-snug text-muted-foreground">{spec.help}</p>
        ) : null}
      </div>
    );
  };

  const applySocPreset = (p) => setRaw((prev) => ({ ...prev, minSocPct: String(p.min), maxSocPct: String(p.max) }));

  return (
    <div className="space-y-6">
      <Card className="technical-card">
        <CardHeader>
          <CardTitle className="font-chivo uppercase tracking-tight flex items-center gap-2">
            <BatteryCharging className="h-5 w-5 text-autoxium-blue" />
            EV Battery Sizing Calculator
          </CardTitle>
          <CardDescription>
            Size a pack's cell configuration, energy, current capability, and mass from a
            range target, energy consumption, motor power, cell data, SOC window, and
            end-of-life margin. Results update live as you edit.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="rounded-md border border-border/60 bg-muted/30 p-3 space-y-2" data-testid="ev-preset-panel">
            <div className="flex flex-col sm:flex-row sm:items-end gap-2">
              <div className="space-y-1 flex-1 min-w-0 sm:max-w-sm">
                <Label className="font-mono text-[11px] uppercase tracking-wider text-muted-foreground leading-tight flex items-center gap-1.5">
                  <CarFront className="h-3.5 w-3.5" /> Vehicle Preset
                </Label>
                <Select value={presetId} onValueChange={loadPreset}>
                  <SelectTrigger className="h-8 text-sm" data-testid="ev-preset-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {BATTERY_SIZING_PRESETS.map((p) => (
                      <SelectItem key={p.id} value={p.id}>{p.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              {presetModified && (
                <Button variant="outline" size="sm" className="h-8 shrink-0" onClick={() => loadPreset(presetId)} data-testid="ev-preset-reset">
                  <RotateCcw className="h-3.5 w-3.5" /> Reset to preset
                </Button>
              )}
            </div>
            {preset && (
              <div className="text-xs text-muted-foreground space-y-1">
                {preset.reference && (
                  <p className="font-mono text-foreground/80" data-testid="ev-preset-reference">
                    Real vehicle: {preset.reference}
                  </p>
                )}
                <p data-testid="ev-preset-note">
                  {preset.note}
                  {presetModified && <span className="ml-2 text-amber-600 dark:text-amber-400">(inputs modified)</span>}
                </p>
              </div>
            )}
          </div>
          {GROUPS.map((group) => (
            <div key={group.id} className="space-y-4 p-4 bg-muted/50 rounded-md">
              <h3 className="font-chivo text-xs font-bold uppercase tracking-wider text-muted-foreground">
                {group.title}
              </h3>
              {group.id === 'vehicle' && (
                <div className="space-y-2">
                  <Label className="font-mono text-xs uppercase tracking-wider text-muted-foreground">Consumption Basis</Label>
                  <Select value={basis} onValueChange={setBasis}>
                    <SelectTrigger className="w-full sm:w-72" data-testid="ev-basis-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {CONSUMPTION_BASES.map((b) => <SelectItem key={b.value} value={b.value}>{b.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  <p className="text-[11px] leading-snug text-muted-foreground" data-testid="ev-basis-help">
                    {BASIS_BY_VALUE[basis].help}
                  </p>
                </div>
              )}
              {group.id === 'soc' && (
                <div className="flex flex-wrap gap-2">
                  {SOC_PRESETS.map((p) => (
                    <Button key={p.label} type="button" variant="outline" size="sm" onClick={() => applySocPreset(p)}
                      data-testid={`ev-soc-preset-${p.min}-${p.max}`}>
                      {p.label}
                    </Button>
                  ))}
                </div>
              )}
              <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
                {group.keys.map(renderField)}
              </div>
            </div>
          ))}
        </CardContent>
      </Card>

      <Card className="technical-card" data-testid="ev-results-card">
          <CardHeader>
            <CardTitle className="font-chivo uppercase tracking-tight text-base">Results</CardTitle>
          </CardHeader>
          <CardContent>
            {hasErrors ? (
              <p className="text-sm text-muted-foreground" data-testid="ev-results-placeholder">
                Fix the highlighted fields to see results.
              </p>
            ) : calcError ? (
              <div className="flex items-center gap-2 text-sm text-destructive" data-testid="ev-calc-error">
                <AlertCircle className="h-4 w-4 shrink-0" /> {calcError}
              </div>
            ) : !results ? (
              <p className="text-sm text-muted-foreground" data-testid="ev-results-loading">
                Calculating…
              </p>
            ) : (
              <div className="space-y-4" data-testid="ev-results">
                <div>
                  <p className="font-mono text-xs uppercase tracking-wider text-muted-foreground mb-1">Pack Configuration</p>
                  <p className="font-mono text-lg font-bold text-autoxium-blue" data-testid="ev-config">
                    {results.cellsInSeries}s{results.cellsInParallel}p · {results.totalCells} cells
                  </p>
                  <Badge variant="outline" className="font-mono text-[10px] mt-1" data-testid="ev-governed-by">
                    sized by {results.governedBy}
                  </Badge>
                </div>

                <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-4 pt-4 border-t border-structure">
                  <Stat label="Installed Energy" value={`${kWh(results.installedEnergyWh)} kWh`}
                    sub={`${results.oversizePct >= 0 ? '+' : ''}${fixed(results.oversizePct, 0)}% vs. required`} />
                  <Stat label="Required Energy" value={`${kWh(results.totalBatteryEnergyWh)} kWh`}
                    sub="incl. SOC window & margins" />
                  <Stat label="Nominal Pack Voltage" value={`${fixed(results.packVoltageNominalV, 1)} V`} />
                  <Stat label="Voltage Limits" value={`${fixed(results.packVoltageMinV, 0)} – ${fixed(results.packVoltageMaxV, 0)} V`}
                    sub="cell cut-off to full charge" />
                  <Stat label="Pack Capacity" value={`${fixed(results.actualPackCapacityAh, 1)} Ah`} />
                  <Stat label="Energy per Cell" value={`${fixed(results.energyPerCellWh, 1)} Wh`} />
                  <Stat label="Est. Range (new)" value={`${fixed(results.rangeBolKm, 0)} km`}
                    sub={`usable ${kWh(results.usableEnergyBolWh)} kWh`} />
                  <Stat label="Est. Range (EOL / cold)" value={`${fixed(results.rangeEolKm, 0)} km`}
                    sub={`usable ${kWh(results.usableEnergyEolWh)} kWh`} />
                  <Stat label="Est. Pack Mass" value={`${fixed(results.packMassKg, 0)} kg`}
                    sub={`cells ${fixed(results.cellMassTotalKg, 0)} kg`} />
                  <Stat label="Pack Specific Energy" value={`${fixed(results.packSpecificEnergyWhKg, 0)} Wh/kg`} />
                </div>

                <div className="grid lg:grid-cols-2 gap-6 pt-4 border-t border-structure">
                <div className="space-y-1.5" data-testid="ev-energy-breakdown">
                  <p className="font-mono text-xs uppercase tracking-wider text-muted-foreground mb-1">Energy Build-up</p>
                  <Row label="Battery energy per km" value={`${fixed(results.batteryEnergyPerKmWh, 1)} Wh/km`} />
                  <Row label="Driving energy" value={`${kWh(results.drivingEnergyWh)} kWh`} />
                  <Row label="Auxiliary energy" value={results.consumptionBasis === 'wheel' ? `${kWh(results.auxiliaryEnergyWh)} kWh` : 'in consumption figure'} />
                  <Row label="Energy for range" value={`${kWh(results.usableEnergyWh)} kWh`} />
                  <Row label="÷ SOC window" value={`${fixed(values.maxSocPct - values.minSocPct, 0)}%`} />
                  <Row label="÷ EOL × cold margin" value={`${fixed(results.capacityMarginPct, 0)}%`} />
                  <Row label="Strings: energy / cont. / peak"
                    value={`${results.parallelStringsForEnergy} / ${results.parallelStringsForContinuous} / ${results.parallelStringsForPeak}`} />
                </div>

                {results.warnings.length > 0 ? (
                  <div className="space-y-2 pt-4 border-t border-structure lg:pt-0 lg:border-t-0" data-testid="ev-warnings">
                    <p className="font-mono text-xs uppercase tracking-wider text-muted-foreground mb-1">Warnings</p>
                    {results.warnings.map((w) => (
                      <div key={w} className="flex items-start gap-2 text-xs text-warning">
                        <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" /> <span>{w}</span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="flex items-start gap-2 text-xs text-emerald-600 pt-4 border-t border-structure lg:pt-0 lg:border-t-0" data-testid="ev-no-warnings">
                    <CheckCircle2 className="h-4 w-4 shrink-0 mt-0.5" /> No sizing warnings.
                  </div>
                )}
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {crossCheck && !hasErrors && !calcError && (
          <Card className="technical-card" data-testid="ev-cross-check">
            <CardHeader>
              <CardTitle className="font-chivo uppercase tracking-tight text-base flex items-center gap-2">
                Motor Current Check
              </CardTitle>
              <CardDescription>
                Battery current at the worst case, the minimum pack voltage
                ({fixed(crossCheck.packVoltageAtCheckV, 0)} V): I = (P<sub>motor</sub> ÷ η<sub>motor+inv</sub> + P<sub>aux</sub>) ÷ V<sub>min</sub>.
                Parallel strings are increased automatically until both limits are met.
              </CardDescription>
            </CardHeader>
            <CardContent className="grid lg:grid-cols-2 gap-6">
              <div className="space-y-3">
              <div className="space-y-1.5">
                <Row label="Continuous battery power" value={`${fixed(crossCheck.continuousElectricalKw, 1)} kW`} />
                <Row label="Continuous draw / capability" value={`${fixed(crossCheck.continuousCurrentA, 0)} / ${fixed(crossCheck.continuousCapabilityA, 0)} A`} />
                <Row label="Utilization" value={`${fixed(crossCheck.continuousUtilizationPct, 0)}%`} />
              </div>
              {crossCheck.continuousOk ? (
                <div className="flex items-center gap-2 text-xs text-emerald-600" data-testid="ev-continuous-ok">
                  <CheckCircle2 className="h-4 w-4 shrink-0" /> Pack sustains the continuous current at its continuous C-rate.
                </div>
              ) : (
                <div className="flex items-center gap-2 text-xs text-warning" data-testid="ev-continuous-warn">
                  <AlertTriangle className="h-4 w-4 shrink-0" /> Continuous current exceeds the pack's continuous rating.
                </div>
              )}
              </div>
              <div className="space-y-3 pt-4 border-t border-structure lg:pt-0 lg:border-t-0">
              <div className="space-y-1.5">
                <Row label="Peak battery power" value={`${fixed(crossCheck.peakElectricalKw, 1)} kW`} />
                <Row label="Peak draw / pulse capability" value={`${fixed(crossCheck.peakCurrentA, 0)} / ${fixed(crossCheck.pulseCapabilityA, 0)} A`} />
                <Row label="Utilization" value={`${fixed(crossCheck.peakUtilizationPct, 0)}%`} />
              </div>
              {crossCheck.peakOk ? (
                <div className="flex items-center gap-2 text-xs text-emerald-600" data-testid="ev-peak-ok">
                  <CheckCircle2 className="h-4 w-4 shrink-0" /> Pack sustains the peak current at its pulse C-rate.
                </div>
              ) : (
                <div className="flex items-center gap-2 text-xs text-warning" data-testid="ev-peak-warn">
                  <AlertTriangle className="h-4 w-4 shrink-0" /> Peak current exceeds the pack's pulse rating.
                </div>
              )}
              </div>
            </CardContent>
          </Card>
        )}

        <Card className="technical-card" data-testid="ev-sources">
          <CardHeader>
            <CardTitle className="font-chivo uppercase tracking-tight text-sm">Sources &amp; Assumptions</CardTitle>
          </CardHeader>
          <CardContent className="grid lg:grid-cols-2 gap-x-8 gap-y-3 text-xs text-muted-foreground leading-relaxed">
            <div>
              <Badge variant="outline" className="font-mono text-[10px] mr-1.5 align-middle">basis</Badge>
              Published Wh/km figures are battery-side (spec sheets, drive-cycle logs) or
              wall-side (EPA, WLTP — they include charging losses). Only a road-load
              calculation gives a wheel-side figure. Picking the wrong basis counts drivetrain
              losses and auxiliary energy twice and oversizes the pack by roughly 20% or more.
            </div>
            <div>
              <Badge variant="outline" className="font-mono text-[10px] mr-1.5 align-middle">margins</Badge>
              The pack is sized so the range target is still met at end of life: required
              energy = energy for range ÷ SOC window ÷ (EOL capacity × (1 − cold loss)). 80%
              EOL matches common 8-year / 70–80% capacity battery warranties.
            </div>
            <div>
              <Badge variant="outline" className="font-mono text-[10px] mr-1.5 align-middle">current</Badge>
              Current is checked at cells in series × cell min voltage, the lowest voltage the
              BMS allows, so it is conservative. Available current = C-rate × capacity (Ah),
              with the continuous rating for continuous power and the pulse rating for peak.
              Uses new-cell capacity; faded cells with higher resistance deliver less.
            </div>
            <div>
              <Badge variant="outline" className="font-mono text-[10px] mr-1.5 align-middle border-amber-500/50 text-amber-700">verify</Badge>
              Default cell data (3.7 V / 3.0 V / 4.2 V, 50 Ah, 0.9 kg, 2C / 4C) represents a
              generic NMC prismatic cell. Cylindrical cells (e.g. 21700) more often spec
              ~3.6 V nominal with a 2.5 V cut-off. Use your cell's datasheet values.
            </div>
            <div>
              <Badge variant="outline" className="font-mono text-[10px] mr-1.5 align-middle">source</Badge>
              500 W default auxiliary load matches NREL's no-A/C baseline accessory draw
              (NREL/CP-540-28960). 46.5 km/h is the WLTC Class 3 average speed.
            </div>
            <div>
              <Badge variant="outline" className="font-mono text-[10px] mr-1.5 align-middle">limits</Badge>
              Energy is taken as nominal voltage × Ah. Because cell voltage is lower at low
              SOC, energy in a SOC window is slightly less than the same share of total
              energy, and voltage sag under load costs another 2–5%. The pack mass is an
              estimate from the cell-to-pack ratio; volume is not estimated. This is a concept-stage
              estimate, not a substitute for drive-cycle simulation, thermal, or BMS design.
            </div>
            <div>
              <Badge variant="outline" className="font-mono text-[10px] mr-1.5 align-middle">presets</Badge>
              Vehicle presets size a new pack (End-of-Life Capacity 100%) so the result can be
              compared with the real one. Consumption comes from EPA ratings (Leaf, Bolt, Model 3)
              or from the EV Powertrain Simulator on WLTC (Nexon, Curvv), matched to real-world
              ranges. Required energy lands within −3% to +5% of the real packs. Values the OEM does
              not publish are marked as estimates in each preset's note.
            </div>
          </CardContent>
        </Card>
    </div>
  );
};

export default EvBatterySizingCalculator;
